import { memo } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

interface VideoCardProps {
  id: string
  title: string
  thumbnailUrl?: string
  duration: number | string
  views: number | string
  quality?: string
  likes?: number
  isPremium?: boolean
  modelName?: string
  index?: number
}

function formatViews(views: number | string): string {
  if (typeof views === 'string') return views
  if (views >= 1000000) return `${(views / 1000000).toFixed(1)}M`
  if (views >= 1000) return `${(views / 1000).toFixed(0)}K`
  return views.toString()
}

function formatDuration(duration: number | string): string {
  if (typeof duration === 'string') return duration
  const mins = Math.floor(duration / 60)
  const secs = duration % 60
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

const VideoCard = memo(function VideoCard({ 
  id, 
  title, 
  thumbnailUrl, 
  duration, 
  views, 
  quality = 'HD',
  likes,
  isPremium = false,
  modelName,
  index = 0 
}: VideoCardProps) {
  const { t } = useTranslation()

  return (
    <Link to={`/video/${id}`} style={{ textDecoration: 'none' }}>
      <article className="video-card">
        <div className="thumbnail-container">
          {thumbnailUrl ? (
            <img 
              src={thumbnailUrl} 
              alt={title} 
              className="thumbnail" 
              loading="lazy"
              decoding="async"
            />
          ) : (
            <div 
              className="thumbnail" 
              style={{ 
                background: `linear-gradient(135deg, hsl(${index * 25}, 30%, 20%) 0%, #1a1a1a 100%)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#555" strokeWidth="1.5">
                <polygon points="5 3 19 12 5 21 5 3"/>
              </svg>
            </div>
          )}
          <span className="quality-badge">{quality}</span>
          {isPremium && <span className="premium-badge">{t('common.premium_badge')}</span>}
          <span className="duration">{formatDuration(duration)}</span>
        </div>
        <div className="video-info">
          <h3 className="video-title">{title}</h3>
          {modelName && (
            <p style={{ fontSize: '0.8rem', color: 'var(--accent-primary)', marginBottom: '4px' }}>
              {modelName}
            </p>
          )}
          <div className="video-meta">
            <span>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                <circle cx="12" cy="12" r="3"/>
              </svg>
              {formatViews(views)}
            </span>
            {likes !== undefined && (
              <span>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                </svg>
                {formatViews(likes)}
              </span>
            )}
          </div>
        </div>
      </article>
    </Link>
  )
})

export default VideoCard
